/**
 * Plan tiers — single source of truth for pricing and entitlements,
 * shared by the marketing site, the app, and (later) Paystack billing.
 * All amounts in ZAR cents. Annual = 2 months free.
 */
export const PLAN_IDS = ['starter', 'small', 'pro', 'fund'];
export const PLANS = {
    starter: {
        id: 'starter',
        // Shown to people as Compliance, one of the two products. The id stays
        // 'starter' because every org document, the rules and the billing codes
        // already carry it.
        name: 'Compliance',
        tagline: 'Every statutory deadline your NPO owes, with the reminders. Free for every NPO.',
        monthlyCents: 0,
        annualCents: 0,
        highlight: false,
        // The free tier is the compliance register and nothing else. That is the
        // mission and it is cheap to serve: a handful of reminder emails. Grant
        // management, which is the part that saves an organisation real hours and
        // is expensive to run, is what you pay for. The previous free tier gave
        // away two funders plus the pipeline and grant records, which is a
        // perfectly usable product forever, so nobody had a reason to upgrade.
        limits: { funders: 0, activeGrants: 0, users: 2, storageMb: 250, callsPerYear: 0 },
        modules: { seeker: false, grantmaker: false, fundraising: false, me: false, finance: false },
        features: [
            'The statutory register: the DSD, CIPC, SARS, B-BBEE and PAIA deadlines your legal form and approvals bring, each with the section of the Act it comes from',
            'Every date worked out from your year-end, with the working day you have to act by',
            'Deadlines and tasks on one calendar, with email reminders 30, 14, 7 and 1 days before, a nudge two days after, and a Monday digest for the team',
            'Document vault (250 MB) that warns you before a bank letter or a tax PIN goes stale',
            'Office-bearers on record, with the one-month notice to DSD tracked whenever one changes',
            '2 team members',
            'Data hosted in Johannesburg, with a POPIA section 72 disclosure',
            'Append-only audit trail your board can read',
            'The governance add-on, free to switch on: risk register, policies, resolutions and the board pack',
        ],
    },
    small: {
        id: 'small',
        // Shown to people as Grant management, the paid core. The id stays 'small'.
        name: 'Grant management',
        tagline: 'Every grant from application to close-out, filled in from its contract.',
        monthlyCents: 249_00,
        annualCents: 2_490_00,
        highlight: false,
        limits: { funders: null, activeGrants: null, users: 5, storageMb: 2_048, callsPerYear: 0 },
        modules: { seeker: true, grantmaker: false, fundraising: false, me: false, finance: false },
        features: [
            'Everything in Compliance',
            'Grants tracked from application to close-out, as many as you have',
            'The contract reader: it fills a grant in from its agreement, with the money, the dates, the restrictions and what the money may be spent on, for you to check and add to',
            'Payments and reporting dates on the calendar, with the same reminders as your statutory deadlines',
            'Your funders on one register, with the NLC 12-month cooling-off worked out for you',
            '5 team members',
            'Document vault (2 GB)',
            'Add-ons when you need them: the pipeline board, report builder, answer library and more, switched on for your workspace',
        ],
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        // Every add-on is included, and an administrator switches on the ones
        // the workspace uses: a new Pro workspace starts on the same short menu
        // as any other, so "switched on" would not be true of it.
        tagline: 'Grant management with every add-on included: switch on the ones you use.',
        monthlyCents: 850_00,
        annualCents: 8_500_00,
        highlight: true,
        limits: { funders: null, activeGrants: null, users: null, storageMb: 10_240, callsPerYear: 0 },
        modules: { seeker: true, grantmaker: false, fundraising: true, me: true, finance: true },
        features: [
            'Everything in Grant management',
            'Unlimited funders, grants & team members',
            'Funders register with SA funder types & NLC cooling-off timers',
            'Answer library with word counts & revision history',
            'Per-funder report builder with live financial figures',
            'Budget vs actual, tranches & restricted-fund tracking',
            'Weekly Monday digest',
            'Funder application packs & readiness checks',
            'One-click board packs, risk register, policy reviews & resolutions log',
            'Leadership dashboard: win rate, renewal radar & funder-concentration alerts',
            'Fundraising: donors with consent records, Section 18A receipts with the Notice 6762 fields, an annual receipt per donor, and the IT3(d) working schedule',
            'M&E indicators & board-ready exports',
            'WhatsApp reminders (rolling out)',
            '10 GB document vault',
            'Priority email support',
        ],
    },
    fund: {
        id: 'fund',
        // Shown to people as Grant makers: the product for the side that gives
        // grants, apart from the two an NPO uses to receive them. The id stays
        // 'fund' because the org documents, the billing codes and orgKind
        // already carry it.
        name: 'Grant makers',
        tagline: 'For foundations, trusts, CSI teams and donors that give grants, from the call for applications to the grantee’s last report.',
        monthlyCents: 4_999_00,
        annualCents: 49_990_00,
        highlight: false,
        limits: { funders: null, activeGrants: null, users: null, storageMb: null, callsPerYear: null },
        modules: { seeker: true, grantmaker: true, fundraising: true, me: true, finance: true },
        // In the voice of the people who give the money. The modules still carry
        // everything Pro does, so a fund that bought this plan before keeps every
        // screen it had; the list says what a grant maker buys it for.
        features: [
            'Your calls for applications on a public portal, with as many applications as come in',
            'Each application screened against your eligibility rules as it arrives',
            'Reviewers score with a conflict-of-interest declaration, and step out where they have one',
            'Board sessions with the pack printed in one click, and decisions recorded so they cannot be changed afterwards',
            'Payments in tranches, each approved by two people, exported for your bank’s bulk payment',
            'Evidence and reports from grantees collected, checked and spot-checked at random',
            'One record per grantee, with every grant, payment and report',
            'The SED register your B-BBEE verifier asks for',
            'What you have paid out against what is evidenced and checked, on one dashboard',
            'Your own statutory register and reminders, as every NPO has on Compliance',
            'Donors and 18A receipts, cash flow, outcomes and governance included, switched on when you use them',
            'Unlimited team members and reviewers',
            'Dedicated onboarding and support',
        ],
    },
};
export function planMonthlyEquivalentCents(plan, cycle) {
    if (plan.monthlyCents === 0)
        return 0;
    return cycle === 'monthly' ? plan.monthlyCents : Math.round(plan.annualCents / 12);
}
/* ============================================================
   The legacy free tier
   ============================================================

   Starter was re-cut to the compliance register on the date below. Before
   that it carried two funders, unlimited grants, the pipeline board and the
   whole seeker toolkit.

   Organisations already on the free plan keep exactly what they signed up
   for. Silently removing a feature that a nonprofit has been running its
   grant pipeline on for a year is not a pricing change, it is taking a
   working tool away from someone who has no budget to replace it, and it is
   the fastest way to lose the word of mouth this product depends on.

   Grandfathering is keyed on the org's creation date rather than a migration
   flag, so it needs no backfill and cannot half-apply if a backfill is
   interrupted. Every workspace created before the cutoff resolves to the
   legacy shape for as long as it stays on Starter; the moment it subscribes,
   the paid plan's own limits take over and this stops mattering. */
/** The date Starter became compliance-only. ISO, Africa/Johannesburg. */
export const FREE_TIER_RECUT_AT = '2026-09-08';
export const LEGACY_STARTER_LIMITS = {
    funders: 2,
    activeGrants: null,
    users: 2,
    storageMb: 500,
    callsPerYear: 0,
};
export const LEGACY_STARTER_MODULES = {
    seeker: true,
    grantmaker: false,
    fundraising: false,
    me: false,
    finance: false,
};
/**
 * True if a workspace created at `createdAtIso` predates the re-cut and so
 * keeps the old free tier. Anything without a creation date is treated as
 * legacy: the safe direction is to give an existing customer too much, not
 * to take something away on the strength of a missing field.
 */
export function isLegacyFreeTier(createdAtIso) {
    if (!createdAtIso)
        return true;
    return createdAtIso.slice(0, 10) < FREE_TIER_RECUT_AT;
}
/** The limits a Starter workspace actually gets, legacy or current. */
export function starterLimitsFor(createdAtIso) {
    return isLegacyFreeTier(createdAtIso) ? LEGACY_STARTER_LIMITS : PLANS.starter.limits;
}
/**
 * The modules a Starter workspace actually gets, legacy or current.
 *
 * The grandfathering is for seekers only. The old free tier was the seeker
 * toolkit, and a grantmaking fund never had it: a fund is created with
 * DEFAULT_MODULES_FUND, which has no seeker. Handing a fund the seeker
 * toolkit when its trial closed gave it Funders, Grants and the pipeline in
 * place of the grantmaking screens it had been using, which is not keeping
 * what it signed up for. A fund goes back to the compliance register.
 */
export function starterModulesFor(createdAtIso, kind = 'seeker') {
    return kind === 'seeker' && isLegacyFreeTier(createdAtIso) ? LEGACY_STARTER_MODULES : PLANS.starter.modules;
}
